import React from 'react';

import { Form, Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import ArrayFields from 'components/shared/ArrayFields';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Label from 'components/shared/FormAdapters/FormDefault/Label';
import LabelWrapper from 'components/shared/FormAdapters/FormDefault/Label/Wrapper';
import FormErrorText from 'components/shared/FormErrorText';
import Loader from 'components/shared/Loader';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import { pageDataSelector } from 'store/page/selectors';
import generateHandleValue from 'utils/form/mutators/generateHandleValue';

import { BuilderFooterButton } from '../../shared/BuilderFooterButton';
import { usePageContext } from '../../shared/context';
import PageFormButtons from '../../shared/PageFormButtons';
import { useHandleAfterNextDetails } from '../../utils';
import { useAboutUsDetails } from './functions';

function Details() {
  useElementBottomPosition(SNACKBAR_POSITION);
  const page = useSelector(pageDataSelector);
  const { useNextStepFormHandler } = usePageContext();
  const onAfterNext = useHandleAfterNextDetails();
  const handleNextStep = useNextStepFormHandler({ onAfterNext });
  const { validate } = useAboutUsDetails();
  const showPopup = useFormFieldPopup();

  return (
    <Form
      onSubmit={async (values) => {
        try {
          await handleNextStep(values);
        } catch (e) {
          console.log('----- handleNextStep e', e);
        }
      }}
      initialValues={page}
      validate={validate}
      mutators={{
        generateHandleValue,
      }}
      render={({ handleSubmit, submitting, invalid, submitError, form }) => (
        <form onSubmit={handleSubmit} autoComplete="nope">
          <UnsavedChangesFormWatcher />

          {submitting && <Loader isFillBlock />}
          <FormTitle center>{page.id ? 'Edit Page Content' : 'Create New Content'}</FormTitle>

          <FormTitle hint="Enter information to organize this content so that you can find it easily later.">
            I. Page Details
          </FormTitle>

          <LabelWrapper>
            <Label>Company Name*</Label>
            <Text accent>The name of a page will appear throughout the client facing itinerary.</Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="name"
            placeholder="Phil West Safaris"
            type="text"
            onCustomChange={form.mutators.generateHandleValue}
          />

          <LabelWrapper>
            <Label>Tags</Label>
            <Text accent>
              Tags allow you to create tailored versions of your content pages for different audiences and/or seasons.
              You can leave blank if this is the only version of this page.
            </Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="tags"
            placeholder="Default"
            type="tags"
            onCustomChange={form.mutators.generateHandleValue}
            onFocus={showPopup(
              <Text accent style={{ fontSize: '1.5rem' }}>
                <b>Tool Tip:</b>
                <br />
                <br />
                Tags are an easy way to create multiple versions of your Content Page. <br />
                <br />
                You can use tags here to create pages about private guides that work with your company, individual
                consultants within your company, or different versions of your About us pages. <br />
                <br />
                For example, you might have a page about Phil West as a private guide or a page about Phil West as a
                safari planner for an FIT group: use tags to differentiate your page content.
              </Text>,
            )}
          />

          <LabelWrapper>
            <Label>@Handle</Label>
            <Text accent>Automatically generated from combining the name and tag fields.</Text>
          </LabelWrapper>
          <Field component={FormGroupAdapter} name="handle" placeholder="@PhilWestSafaris" type="text" readOnly />

          <FormTitle hint="Add on the ground emergency contact for Agents to share with Guests.">
            II. Emergency Contact
          </FormTitle>

          <ArrayFields
            initialValues={page}
            data={{
              sectionName: 'emergencyContacts',

              config: {
                withBlockRemoving: false,
                withBlockAdding: false,
                twoColumn: true,
              },

              fieldsData: [
                {
                  name: 'name',
                  placeholder: 'John Doe',
                  label: 'Name',
                },
                {
                  name: 'phone',
                  placeholder: '555-555-5555',
                  label: 'Phone Number',
                },
                {
                  name: 'email',
                  placeholder: 'john.doe@company.com',
                  label: 'Email',
                  autoComplete: 'none',
                },
              ],
            }}
          />

          {submitError && <FormErrorText>{submitError}</FormErrorText>}

          <StickyFooter>
            <FormActions isRight>
              <BuilderFooterButton htmlType="submit" id="aboutUsNextButton" disabled={submitting || invalid}>
                Next
              </BuilderFooterButton>
            </FormActions>
          </StickyFooter>

          <PageFormButtons />
        </form>
      )}
    />
  );
}

export default Details;
